import axios, { AxiosError } from 'axios';

const TOKEN_KEY = 'eventum_admin_token';

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      window.dispatchEvent(new Event('admin-logout'));
    }
    return Promise.reject(err);
  },
);

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** Admin login: email + password, returns JWT (no Telegram). */
export async function adminLogin(
  email: string,
  password: string,
): Promise<{ user: unknown; accessToken: string }> {
  const res = await axios.post(
    `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000'}/auth/admin/login`,
    { email: email.trim(), password },
  );
  const { accessToken } = res.data;
  setStoredToken(accessToken);
  return res.data;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export async function fetchAdminMarkets(params: PaginationParams = {}) {
  const res = await api.get<PaginatedResponse<unknown>>('/admin/markets', {
    params: { page: params.page, limit: params.limit, search: params.search },
  });
  return res.data;
}

export async function fetchAdminMarket(id: number) {
  const res = await api.get(`/admin/markets/${id}`);
  return res.data;
}

export type MarketStatus =
  | 'OPEN'
  | 'RESOLVED'
  | 'VOID'
  | 'CLOSED_FOR_TRADING'
  | 'PENDING_MODERATION'
  | 'REJECTED';

export async function createMarket(body: {
  question: string;
  description?: string | null;
  category?: string | null;
  imageUrl?: string | null;
  deadline: string;
  status?: MarketStatus;
  sourceLinks?: string[] | null;
  createdById?: number | null;
  initialYesPercent?: number;
  targetYesPercent?: number;
  targetNoPercent?: number;
  botEnabled?: boolean;
  botMinOrderAmount?: string;
  botMaxOrderAmount?: string;
  botCooldownMs?: number;
  botMaxExposure?: string;
  manualVolume?: string | null;
}) {
  const res = await api.post('/admin/markets', body);
  return res.data;
}

export async function updateMarket(
  id: number,
  body: {
    question?: string;
    description?: string | null;
    category?: string | null;
    imageUrl?: string | null;
    deadline?: string;
    status?: MarketStatus;
    sourceLinks?: string[] | null;
    initialYesPercent?: number;
    targetYesPercent?: number;
    targetNoPercent?: number;
    botEnabled?: boolean;
    botMinOrderAmount?: string;
    botMaxOrderAmount?: string;
    botCooldownMs?: number;
    botMaxExposure?: string;
    manualVolume?: string | null;
  },
) {
  const res = await api.patch(`/admin/markets/${id}`, body);
  return res.data;
}

export async function deleteMarket(id: number) {
  const res = await api.delete(`/admin/markets/${id}`);
  return res.data;
}

export async function fetchAdminUsers(params: PaginationParams = {}) {
  const res = await api.get<PaginatedResponse<unknown>>('/admin/users', {
    params: { page: params.page, limit: params.limit, search: params.search },
  });
  return res.data;
}

export async function fetchAdminBots(params: PaginationParams = {}) {
  const res = await api.get<PaginatedResponse<unknown>>('/admin/bots', {
    params: { page: params.page, limit: params.limit, search: params.search },
  });
  return res.data;
}

export async function createBot(body: {
  telegramId: string;
  username?: string | null;
  balanceAvailable: string;
}) {
  const res = await api.post('/admin/bots', body);
  return res.data;
}

export async function updateBot(
  botId: number,
  body: { username?: string | null; balanceAvailable?: string },
) {
  const res = await api.patch(`/admin/bots/${botId}`, body);
  return res.data;
}

export async function deleteBot(botId: number) {
  const res = await api.delete(`/admin/bots/${botId}`);
  return res.data;
}

export async function autoCreateBots(params?: { count?: number; balanceAvailable?: string }) {
  const res = await api.post('/admin/bots/auto', params ?? {});
  return res.data;
}

export async function fetchAdminOrders(params: PaginationParams = {}) {
  const res = await api.get<PaginatedResponse<unknown>>('/admin/orders', {
    params: { page: params.page, limit: params.limit, search: params.search },
  });
  return res.data;
}

export async function fetchAdminPositions(params: PaginationParams = {}) {
  const res = await api.get<PaginatedResponse<unknown>>('/admin/positions', {
    params: { page: params.page, limit: params.limit, search: params.search },
  });
  return res.data;
}

export async function fetchResolutionLogs(params: PaginationParams = {}) {
  const res = await api.get<PaginatedResponse<unknown>>('/admin/resolution-logs', {
    params: { page: params.page, limit: params.limit, search: params.search },
  });
  return res.data;
}

export async function fetchUserLimits(params: PaginationParams = {}) {
  const res = await api.get<PaginatedResponse<unknown>>('/admin/user-limits', {
    params: { page: params.page, limit: params.limit, search: params.search },
  });
  return res.data;
}

export async function fetchTransactions(
  params: PaginationParams & { amount?: string; type?: string; user?: string } = {},
) {
  const res = await api.get<PaginatedResponse<unknown>>('/admin/transactions', {
    params: {
      page: params.page,
      limit: params.limit,
      search: params.search,
      amount: params.amount || undefined,
      type: params.type || undefined,
      user: params.user || undefined,
    },
  });
  return res.data;
}

export async function createOrder(body: {
  userId: number;
  marketId: number;
  side: 'YES' | 'NO';
  amount: string;
  categoryId?: number;
}) {
  const res = await api.post('/admin/orders', body);
  return res.data;
}

export async function cancelOrder(orderId: number) {
  const res = await api.patch(`/admin/orders/${orderId}/cancel`);
  return res.data;
}

export async function deleteOrder(orderId: number) {
  const res = await api.delete(`/admin/orders/${orderId}`);
  return res.data;
}

export async function updateOrderCategory(orderId: number, categoryId: number | null) {
  const res = await api.patch(`/admin/orders/${orderId}/category`, { categoryId });
  return res.data;
}

export async function fetchAdminNotifications(params: PaginationParams = {}) {
  const res = await api.get<PaginatedResponse<unknown>>('/admin/notifications', {
    params: { page: params.page, limit: params.limit, search: params.search },
  });
  return res.data;
}

export interface CreateNotificationResponse {
  notification: unknown;
  telegram: {
    requested: boolean;
    attempted: number;
    sent: number;
    failed: number;
    failures: Array<{ chatId: string; status?: number; error: string }>;
  } | null;
}

export async function createNotification(body: {
  title: string;
  body?: string;
  type?: string;
  notifyTelegram?: boolean;
}) {
  const res = await api.post<CreateNotificationResponse>('/admin/notifications', body);
  return res.data;
}

export async function updateNotification(id: number, body: { title?: string; body?: string; type?: string }) {
  const res = await api.patch(`/admin/notifications/${id}`, body);
  return res.data;
}

export async function deleteNotification(id: number) {
  const res = await api.delete(`/admin/notifications/${id}`);
  return res.data;
}

export async function fetchOrderCategories(params: PaginationParams = {}) {
  const res = await api.get<PaginatedResponse<unknown>>('/admin/order-categories', {
    params: { page: params.page, limit: params.limit, search: params.search },
  });
  return res.data;
}

export async function createOrderCategory(body: { name: string; code: string; description?: string }) {
  const res = await api.post('/admin/order-categories', body);
  return res.data;
}

export async function updateOrderCategoryApi(id: number, body: { name?: string; code?: string; description?: string }) {
  const res = await api.patch(`/admin/order-categories/${id}`, body);
  return res.data;
}

export async function deleteOrderCategory(id: number) {
  const res = await api.delete(`/admin/order-categories/${id}`);
  return res.data;
}

export async function getMe(): Promise<{ id: number; role: string; telegramId?: string }> {
  const res = await api.get('/users/me');
  return res.data;
}

export async function resolveMarket(
  marketId: number,
  body: { resolverUserId: number; resolution: 'YES' | 'NO' | 'VOID'; comment?: string },
) {
  const res = await api.patch(`/admin/markets/${marketId}/resolve`, body);
  return res.data;
}

export async function moderateMarket(
  marketId: number,
  body: { approved: boolean; comment?: string },
) {
  const res = await api.patch(`/admin/markets/${marketId}/moderate`, body);
  return res.data;
}

export async function withdrawMarketWalletToDeposit(
  marketId: number,
  body: { amount: string; destinationAddress?: string },
) {
  const res = await api.post(`/admin/markets/${marketId}/withdraw-to-deposit`, body);
  return res.data;
}

export async function updateUserRevshare(
  userId: number,
  revsharePercent: number | null,
) {
  const res = await api.patch(`/admin/users/${userId}/revshare`, {
    revsharePercent,
  });
  return res.data;
}

export async function updateUserEmail(userId: number, email: string | null) {
  const res = await api.patch(`/admin/users/${userId}/email`, { email });
  return res.data;
}

/** Записать вывод средств пользователю (админ вручную фиксирует факт вывода). */
export async function recordWithdrawal(body: {
  userId: number;
  amount: string;
  comment?: string | null;
}) {
  const res = await api.post('/admin/withdrawals', body);
  return res.data;
}

export interface UsdtWithdrawalRequestRow {
  id: number;
  amountTon: string;
  destinationAddress: string;
  destinationRaw: string | null;
  status: string;
  txHash: string | null;
  error: string | null;
  linkedTransactionId: number | null;
  createdAt: string;
  updatedAt: string;
  user?: { id: number; username?: string | null; telegramId?: string };
}

export async function fetchAdminUsdtWithdrawals(
  params: PaginationParams & { status?: string } = {},
) {
  const res = await api.get<PaginatedResponse<UsdtWithdrawalRequestRow>>(
    '/admin/usdt-withdrawals',
    {
      params: {
        page: params.page,
        limit: params.limit,
        status: params.status,
      },
    },
  );
  return res.data;
}

export async function approveUsdtWithdrawal(id: number) {
  const res = await api.post(`/admin/usdt-withdrawals/${id}/approve`);
  return res.data as UsdtWithdrawalRequestRow;
}

export async function rejectUsdtWithdrawal(id: number) {
  const res = await api.post(`/admin/usdt-withdrawals/${id}/reject`);
  return res.data as UsdtWithdrawalRequestRow;
}

export async function fetchContentCategories(params: PaginationParams = {}) {
  const res = await api.get<PaginatedResponse<unknown>>('/admin/content-categories', {
    params: { page: params.page, limit: params.limit, search: params.search },
  });
  return res.data;
}

export async function createContentCategory(body: { code: string; name: string }) {
  const res = await api.post('/admin/content-categories', body);
  return res.data;
}

export async function updateContentCategory(id: number, body: { code?: string; name?: string }) {
  const res = await api.patch(`/admin/content-categories/${id}`, body);
  return res.data;
}

export async function deleteContentCategory(id: number) {
  await api.delete(`/admin/content-categories/${id}`);
}

export async function fetchContentItems(params: PaginationParams & { categoryId?: number } = {}) {
  const res = await api.get<PaginatedResponse<unknown>>('/admin/content-items', {
    params: { page: params.page, limit: params.limit, categoryId: params.categoryId },
  });
  return res.data;
}

export async function createContentItem(body: {
  categoryId: number;
  title: string;
  body?: string | null;
  sortOrder?: number;
}) {
  const res = await api.post('/admin/content-items', body);
  return res.data;
}

export async function updateContentItem(
  id: number,
  body: { title?: string; body?: string | null; sortOrder?: number; categoryId?: number },
) {
  const res = await api.patch(`/admin/content-items/${id}`, body);
  return res.data;
}

export async function deleteContentItem(id: number) {
  await api.delete(`/admin/content-items/${id}`);
}
